import {
  ArrowLeftRight, ArrowRight, Bot, Check, Cog, FilePlus, FileText, Flag,
  GitBranch, GitMerge, Globe, Paperclip, PaperclipIcon, Pencil, Plus, RefreshCw,
  Rocket, Settings2, Sparkles, Tag, Trash2, User, Wand2, X,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { cn } from "@/lib/cn"
import { fmtRelativeTime } from "@/lib/format"
import { wordDiff, type DiffSegment } from "@/lib/word-diff"
import type {
  Activity,
  ActivityChange,
  ActorKind,
} from "@/types/activity"

type Props = {
  entries: Activity[]
  // When true, renders compact cards (right-rail use); when false, renders
  // wider rows suitable for a modal / drawer body.
  compact?: boolean
}

// Vertical timeline of activity. One card per Activity (parent row); each
// card lists its child ActivityChange rows inside. The left-rail icon
// reflects the kind of change (state flip, priority bump, tag add, …),
// not the actor — the actor lives as a chip inside the card.
export function ActivityFeedPane({ entries, compact = false }: Props) {
  if (entries.length === 0) {
    return (
      <div className="rounded-control border border-dashed border-border p-4 text-center text-xs text-app-muted">
        No activity yet — every save will show up here.
      </div>
    )
  }

  return (
    <div className={cn("relative pl-10", compact ? "max-h-[60vh] overflow-y-auto pr-1" : null)}>
      {/* Continuous rail behind every card. */}
      <div className="absolute left-4 top-3 bottom-3 w-px bg-border" aria-hidden />
      <ol className="space-y-3">
        {entries.map((a) => (
          <ActivityCard key={a.id} activity={a} compact={compact} />
        ))}
      </ol>
    </div>
  )
}

const ACTOR_TONE: Record<ActorKind, string> = {
  forge:        "bg-vip-gold/10 text-vip-gold border-vip-gold/30",
  company_user: "bg-white/5 text-app-text border-border",
  system:       "bg-white/[0.03] text-app-subtle border-border",
  llm_tool:     "bg-sky-500/10 text-sky-300 border-sky-500/30",
  api:          "bg-sky-500/10 text-sky-300 border-sky-500/30",
  automation:   "bg-white/[0.03] text-app-subtle border-border",
}

const ACTOR_ICON: Record<ActorKind, LucideIcon> = {
  forge:        Wand2,
  company_user: User,
  system:       Cog,
  llm_tool:     Sparkles,
  api:          Globe,
  automation:   Bot,
}

// Per-action rail icon. Matches the mockup vocabulary: swap-arrows for
// reassign/priority, refresh for state, pencil for free-form fields, etc.
const ACTION_ICON: Record<string, LucideIcon> = {
  reassign:               ArrowLeftRight,
  priority_change:        Flag,
  state_change:           RefreshCw,
  field_update:           Pencil,
  workflow_update:        FileText,
  prompt_section_update:  FileText,
  tool_keys_update:       Settings2,
  tool_config_update:     Settings2,
  data_collection_update: Settings2,
  set_collection:         Tag,
  create:                 Plus,
  destroy:                Trash2,
  version_create:         FilePlus,
  version_promote:        Rocket,
  version_archive:        GitBranch,
  version_restore_draft:  GitBranch,
  merge_into:             GitMerge,
  attachment_set:         Paperclip,
  attachment_clear:       PaperclipIcon,
}

// Friendly action title for the card. The mockup leads with this, then
// renders the field chip + diff underneath.
function actionTitle(action: string, field: string | null | undefined): string {
  switch (action) {
    case "reassign":              return "Assignee changed"
    case "priority_change":       return "Priority updated"
    case "state_change":          return "Status changed"
    case "field_update":          return field ? `${prettyField(field)} updated` : "Field updated"
    case "workflow_update":       return "Workflow updated"
    case "prompt_section_update": return "Prompt edited"
    case "tool_keys_update":      return "Tools updated"
    case "tool_config_update":    return "Tool config updated"
    case "data_collection_update":return "Data collection updated"
    case "set_collection":        return field === "tags" ? "Tag added" : "Collection updated"
    case "create":                return "Created"
    case "destroy":               return "Deleted"
    case "version_create":        return "Draft version created"
    case "version_promote":       return "Promoted to live"
    case "version_archive":       return "Previous version archived"
    case "version_restore_draft": return "Restored to draft"
    case "merge_into":            return "Merged"
    case "attachment_set":        return "Attachment uploaded"
    case "attachment_clear":      return "Attachment cleared"
    default:                       return action.replace(/_/g, " ")
  }
}

function ActivityCard({ activity, compact }: { activity: Activity; compact: boolean }) {
  const tone        = ACTOR_TONE[activity.actorKind] ?? ACTOR_TONE.company_user
  const ActorIcon   = ACTOR_ICON[activity.actorKind] ?? User
  const actorLabel  = formatActorLabel(activity)
  const role        = activity.companyUser?.role
  const note        = activity.note?.trim() || ""
  const initials    = actorInitials(activity)

  // Pick the rail icon from the first child change; multi-change activities
  // (e.g. triage stamping) still get one rail entry per Activity.
  const leadAction  = activity.changes[0]?.action ?? "field_update"
  const RailIcon    = ACTION_ICON[leadAction] ?? Pencil

  return (
    <li className="relative">
      {/* Rail marker — small circle on the vertical line. */}
      <div className="absolute -left-10 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-full border border-vip-gold/40 bg-vip-gold/10 text-vip-gold">
        <RailIcon size={13} strokeWidth={2} aria-hidden />
      </div>

      <div className={cn(
        "rounded-control border border-border bg-app-bg",
        compact ? "p-3" : "p-4",
      )}>
        {/* Actor row: avatar + name + role + trigger + relative time. */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <Avatar initials={initials} kind={activity.actorKind} />
            <span className="font-semibold uppercase tracking-wide text-app-text">{actorLabel}</span>
            {role && activity.actorKind === "company_user" && (
              <span className={cn("rounded-control border px-1.5 py-0.5 lowercase tracking-wide", tone)}>
                {role}
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-control border border-border bg-white/[0.03] px-1.5 py-0.5 text-app-subtle">
              <ActorIcon size={11} strokeWidth={2} />
              via {prettyTrigger(activity.triggerKind)}
            </span>
          </div>
          <span className="shrink-0 text-[11px] text-app-muted">{fmtRelativeTime(activity.at)}</span>
        </div>

        {note && (
          <div className="mt-2 rounded-control border-l-2 border-vip-gold/60 bg-vip-gold/[0.06] px-3 py-2 text-xs leading-relaxed text-app-text whitespace-pre-wrap">
            {note}
          </div>
        )}

        {activity.changes.length > 0 && (
          <div className="mt-3 space-y-3">
            {activity.changes.map((c) => (
              <ChangeBlock key={c.id} change={c} />
            ))}
          </div>
        )}
      </div>
    </li>
  )
}

function ChangeBlock({ change }: { change: ActivityChange }) {
  const title = actionTitle(change.action, change.field)
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <h4 className="text-base font-semibold text-app-text">{title}</h4>
        {change.field && (
          <span className="rounded bg-white/[0.04] px-1.5 py-0.5 font-mono text-[10px] text-app-muted">
            {change.field}
          </span>
        )}
      </div>
      <div className="mt-2">
        <ChangeBody change={change} />
      </div>
    </div>
  )
}

const PROSE_FIELDS = new Set(["workflow_yaml", "description", "notes", "name"])

function ChangeBody({ change }: { change: ActivityChange }) {
  switch (change.action) {
    case "create":
    case "destroy":
      return <SnapshotView snapshot={change.action === "create" ? change.after : change.before} />
    case "tool_keys_update":
    case "set_collection":
      return <ChipDiff before={change.before} after={change.after} />
    case "workflow_update":
    case "prompt_section_update":
      return <ProseDiff before={asString(change.before)} after={asString(change.after)} />
    case "field_update":
    case "state_change":
    case "reassign":
    case "priority_change":
      return <FieldDiff change={change} />
    case "tool_config_update":
    case "data_collection_update":
      return <JsonDiff before={change.before} after={change.after} />
    case "version_create":
    case "version_promote":
    case "version_archive":
    case "version_restore_draft":
    case "merge_into":
    case "attachment_set":
    case "attachment_clear":
      return null
    default:
      return <JsonDiff before={change.before} after={change.after} />
  }
}

function FieldDiff({ change }: { change: ActivityChange }) {
  const { before, after, field } = change
  const isProse = field && PROSE_FIELDS.has(field) && (typeof before === "string" || typeof after === "string")
  if (isProse) {
    return <ProseDiff before={asString(before)} after={asString(after)} />
  }
  return <BeforeAfterPills before={before} after={after} />
}

// Mockup-style "Before X → After Y" inline pills. Each side is a small
// labeled box; the arrow between them is centered. Stacks on narrow widths.
function BeforeAfterPills({ before, after }: { before: unknown; after: unknown }) {
  return (
    <div className="flex flex-wrap items-stretch gap-2 text-xs">
      <SidePill label="Before" tone="error">{formatScalar(before)}</SidePill>
      <div className="flex items-center text-app-subtle">
        <ArrowRight size={14} strokeWidth={2} />
      </div>
      <SidePill label="After" tone="success">{formatScalar(after)}</SidePill>
    </div>
  )
}

function SidePill({ label, tone, children }: { label: string; tone: "error" | "success"; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2 rounded-control border border-border bg-white/[0.02] px-2 py-1.5">
      <span className="text-[10px] uppercase tracking-wide text-app-subtle">{label}</span>
      <span className={cn(
        "inline-flex max-w-full items-center gap-1 truncate rounded px-1.5 py-0.5 text-[11px]",
        tone === "error"   ? "bg-status-error/10 text-status-error"   : null,
        tone === "success" ? "bg-status-success/10 text-status-success" : null,
      )}>
        {children}
      </span>
    </div>
  )
}

function ProseDiff({ before, after }: { before: string; after: string }) {
  const segments: DiffSegment[] = wordDiff(before, after)
  const isCreation = before.trim().length === 0
  const stats = isCreation ? null : countChanges(segments)
  return (
    <div className="text-xs">
      <div className="mb-1 flex items-center gap-2 text-[10px] text-app-subtle">
        {isCreation
          ? <span className="text-vip-goldSoft">new content</span>
          : <span>edits</span>}
        {stats && stats.adds > 0 && <span className="text-status-success">+{stats.adds}</span>}
        {stats && stats.dels > 0 && <span className="text-status-error">−{stats.dels}</span>}
      </div>
      <div className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded border border-border bg-app-bg p-3 text-[12px] leading-relaxed text-app-text">
        {segments.length === 0 ? (
          <span className="text-app-muted">(no change)</span>
        ) : (
          segments.map((s, i) => <DiffChunk key={i} seg={s} />)
        )}
      </div>
    </div>
  )
}

function DiffChunk({ seg }: { seg: DiffSegment }) {
  if (seg.type === "equal") return <span className="text-app-muted">{seg.text}</span>
  if (seg.type === "add") {
    return <span className="rounded bg-status-success/15 text-status-success">{seg.text}</span>
  }
  return (
    <span className="rounded bg-status-error/15 text-status-error line-through decoration-status-error/60">
      {seg.text}
    </span>
  )
}

function countChanges(segs: DiffSegment[]): { adds: number; dels: number } {
  let adds = 0, dels = 0
  for (const s of segs) {
    if (s.type === "add")      adds += s.text.replace(/\s+/g, " ").trim().split(" ").filter(Boolean).length
    else if (s.type === "del") dels += s.text.replace(/\s+/g, " ").trim().split(" ").filter(Boolean).length
  }
  return { adds, dels }
}

function ChipDiff({ before, after }: { before: unknown; after: unknown }) {
  const beforeArr = Array.isArray(before) ? (before as unknown[]).map(String) : []
  const afterArr  = Array.isArray(after)  ? (after  as unknown[]).map(String) : []
  const added   = afterArr.filter((k) => !beforeArr.includes(k))
  const removed = beforeArr.filter((k) => !afterArr.includes(k))

  if (added.length === 0 && removed.length === 0) {
    return <span className="text-[11px] text-app-subtle">No element changes.</span>
  }
  return (
    <div className="space-y-1.5">
      {added.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          <span className="inline-flex items-center gap-1 text-status-success">
            <Plus size={11} strokeWidth={2} />Added
          </span>
          {added.map((k) => (
            <span key={`a-${k}`} className="rounded bg-status-success/10 px-1.5 py-0.5 text-status-success">{k}</span>
          ))}
        </div>
      )}
      {removed.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          <span className="inline-flex items-center gap-1 text-status-error">
            <X size={11} strokeWidth={2} />Removed
          </span>
          {removed.map((k) => (
            <span key={`r-${k}`} className="rounded bg-status-error/10 px-1.5 py-0.5 text-status-error line-through">{k}</span>
          ))}
        </div>
      )}
    </div>
  )
}

function SnapshotView({ snapshot }: { snapshot: unknown }) {
  if (snapshot === null || snapshot === undefined) {
    return <span className="text-[11px] text-app-subtle">No snapshot recorded.</span>
  }
  if (typeof snapshot !== "object") {
    return <pre className="rounded bg-white/[0.02] p-2 text-[11px] text-app-muted">{String(snapshot)}</pre>
  }
  const rows = Object.entries(snapshot as Record<string, unknown>)
  if (rows.length === 0) {
    return <span className="text-[11px] text-app-subtle">Empty snapshot.</span>
  }
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-[11px]">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="font-mono text-app-subtle">{k}</dt>
          <dd className="break-all text-app-text">{formatScalar(v)}</dd>
        </div>
      ))}
    </dl>
  )
}

function JsonDiff({ before, after }: { before: unknown; after: unknown }) {
  const hasBefore = before !== null && before !== undefined
  const hasAfter  = after  !== null && after  !== undefined
  if (!hasBefore && !hasAfter) {
    return <span className="text-[11px] text-app-subtle">No diff data.</span>
  }
  return (
    <div className="grid gap-2 text-[11px] md:grid-cols-2">
      {hasBefore && (
        <div>
          <div className="mb-0.5 text-[10px] uppercase tracking-wide text-status-error">before</div>
          <pre className="max-h-60 overflow-auto whitespace-pre-wrap rounded bg-status-error/5 p-2 text-app-muted">
            {JSON.stringify(before, null, 2)}
          </pre>
        </div>
      )}
      {hasAfter && (
        <div>
          <div className="mb-0.5 text-[10px] uppercase tracking-wide text-status-success">after</div>
          <pre className="max-h-60 overflow-auto whitespace-pre-wrap rounded bg-status-success/5 p-2 text-app-muted">
            {JSON.stringify(after, null, 2)}
          </pre>
        </div>
      )}
    </div>
  )
}

function Avatar({ initials, kind }: { initials: string; kind: ActorKind }) {
  const tone =
    kind === "forge"        ? "border-vip-gold/40 bg-vip-gold/10 text-vip-gold" :
    kind === "llm_tool"     ? "border-sky-500/40 bg-sky-500/10 text-sky-300"   :
    kind === "company_user" ? "border-brand-red/40 bg-brand-redSoft text-brand-red" :
                              "border-border bg-white/5 text-app-muted"
  return (
    <span className={cn(
      "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold",
      tone,
    )}>
      {initials}
    </span>
  )
}

function actorInitials(a: Activity): string {
  if (a.actorKind === "company_user" && a.companyUser?.name) {
    const parts = a.companyUser.name.trim().split(/\s+/).slice(0, 2)
    return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || a.companyUser.name[0]?.toUpperCase() || "?"
  }
  if (a.actorKind === "forge")      return "F"
  if (a.actorKind === "llm_tool")   return "AI"
  if (a.actorKind === "automation") return "Au"
  if (a.actorKind === "api")        return "API"
  return "Sy"
}

function formatActorLabel(a: Activity): string {
  if (a.actorKind === "company_user") return a.companyUser?.name ?? "Operator"
  if (a.actorKind === "forge")        return "Forge"
  if (a.actorKind === "llm_tool")     return "LLM Tool"
  if (a.actorKind === "automation")   return "Automation"
  if (a.actorKind === "api")          return "API"
  return "System"
}

function prettyTrigger(t: string): string {
  return t.replace(/_/g, " ")
}

function prettyField(f: string): string {
  return f.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())
}

function asString(v: unknown): string {
  return typeof v === "string" ? v : v == null ? "" : JSON.stringify(v)
}

function formatScalar(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—"
  if (typeof v === "string") return v.length > 200 ? `${v.slice(0, 200)}…` : v
  return JSON.stringify(v)
}

// Suppress "unused" warnings on icons referenced via the ACTION_ICON map.
void Check
